import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Field, inputCls } from "@/components/LegsEditor";
import { useApi, errMsg } from "@/lib/api";
import { code, fmtTime, nowIn, nowLocal } from "@/components/TrackingBits";

const MOVE_COPY = {
  departed: ["Mark departed", "Departure time (local at departure airport)"],
  landed: ["Mark landed", "Landing time (local at arrival airport)"],
  delayed: ["Mark delayed", "When the delay was reported"],
  diverted: ["Mark diverted", "When the diversion was reported"],
  cancelled: ["Cancel this leg", "When it was cancelled"],
  scheduled: ["Reset to scheduled", "When it was reset"],
  eta: ["Update ETA", ""],
  in_position: ["Plane in position", "When the plane got in position (local at departure airport)"],
};

export function MoveDialog({ move, t, onClose, onSaved }) {
  const api = useApi();
  const [at, setAt] = useState(nowLocal());
  const [extra, setExtra] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (move) {
      const lg = t.legs[move.i];
      setAt(nowIn(move.status === "landed" ? lg.progress?.to?.tz : lg.progress?.from?.tz));
      setExtra(move.status === "eta" ? lg.eta || "" : move.status === "in_position" ? lg.in_position?.note || "" : "");
    }
  }, [move, t]);
  if (!move) return null;
  const { i, status } = move;
  const leg = t.legs[i];
  const arr = code(leg.diverted_to || leg.to_airport);
  const needs = { departed: [["dep_fbo", "departure"], ["arr_fbo", "arrival"]], landed: [["arr_fbo", "arrival"]] }[status] || [];
  const read = t.itinerary_read?.status === "done" ? t.itinerary_read.result?.legs?.[i] : null;
  const missing = needs.filter(([k]) => !leg[k]?.name?.trim() && !read?.[k]?.name?.trim()).map(([, label]) => label);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { status, at: at.replace("T", " "), new_time: status === "delayed" ? extra : "", diverted_to: status === "diverted" ? extra : "", eta: status === "departed" ? extra : "" };
      const { data } = status === "eta" ? await api.post(`/tracking/${t.id}/legs/${i}/eta`, { eta: extra })
        : status === "in_position" ? await api.post(`/tracking/${t.id}/legs/${i}/in-position`, { at: at.replace("T", " "), note: extra })
          : await api.post(`/tracking/${t.id}/legs/${i}/status`, body);
      toast.success(["departed", "landed", "eta", "in_position"].includes(status) ? "Saved. The broker has been alerted." : "Leg updated");
      onSaved(data);
      onClose();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[#111319] border-white/10 max-w-md" data-testid="move-dialog">
        <DialogHeader>
          <p className="eyebrow">Leg {i + 1} · {code(leg.from_airport)} → {code(leg.to_airport)}</p>
          <DialogTitle className="font-serif-display text-3xl font-normal text-white">{MOVE_COPY[status][0]}</DialogTitle>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4 mt-2">
          {status !== "eta" && (
            <Field label={MOVE_COPY[status][1]}>
              <Input type="datetime-local" className={inputCls} value={at} onChange={(e) => setAt(e.target.value)} required data-testid="move-at-input" />
            </Field>
          )}
          {status === "eta" && (
            <Field label={`New ETA (local at ${arr})`}>
              <Input type="time" className={inputCls} value={extra} onChange={(e) => setExtra(e.target.value)} required data-testid="move-new-eta-input" />
              <p className="text-[11px] text-zinc-500 mt-2">{leg.eta ? `Current ETA ${fmtTime(leg.eta)}. ` : ""}The broker gets a "new ETA" alert.</p>
            </Field>
          )}
          {status === "departed" && (
            <Field label={`ETA (local at ${code(leg.to_airport)}, optional)`}>
              <Input type="time" className={inputCls} value={extra} onChange={(e) => setExtra(e.target.value)} data-testid="move-eta-input" />
              <p className="text-[11px] text-zinc-500 mt-2">Included in the departure alert sent to the broker.</p>
            </Field>
          )}
          {status === "delayed" && <Field label="New departure time (local)"><Input type="time" className={inputCls} value={extra} onChange={(e) => setExtra(e.target.value)} required data-testid="move-new-time-input" /></Field>}
          {status === "diverted" && <Field label="Diverted to (airport)"><Input className={inputCls} value={extra} onChange={(e) => setExtra(e.target.value)} placeholder="KFLL" required data-testid="move-diverted-input" /></Field>}
          {status === "in_position" && (
            <Field label="Note for the broker (optional)">
              <Textarea className={inputCls} rows={3} maxLength={500} value={extra} onChange={(e) => setExtra(e.target.value)} placeholder="At Signature FBO, crew on board, catering loaded" data-testid="move-in-position-note" />
              <p className="text-[11px] text-zinc-500 mt-2">The broker gets an "in position" email with this note.</p>
            </Field>
          )}
          {missing.length > 0 && (
            <p className="text-xs text-rose-500" data-testid="move-fbo-required">
              The {missing.join(" and ")} FBO name{missing.length > 1 ? "s are" : " is"} missing. Upload the operator itinerary or add it in Trip details. It goes in the broker's alert.
            </p>
          )}
          <div className="flex justify-end gap-3">
            <button type="button" className="frame-btn" onClick={onClose} data-testid="move-cancel-btn">Back</button>
            <button type="submit" className="frame-btn gold" disabled={busy || missing.length > 0} data-testid="move-save-btn">{busy ? "Saving…" : "Save"}</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ContactRow({ label, value, onChange, testId }) {
  return (
    <div className="grid grid-cols-2 gap-2">
      <Input className={inputCls} placeholder={`${label} name`} value={value.name} onChange={(e) => onChange({ ...value, name: e.target.value })} data-testid={`${testId}-name`} />
      <Input className={inputCls} placeholder="Phone" value={value.phone} onChange={(e) => onChange({ ...value, phone: e.target.value })} data-testid={`${testId}-phone`} />
    </div>
  );
}

function FboRow({ value, onChange, testId }) {
  return (
    <div className="space-y-2">
      <ContactRow label="FBO" value={value} onChange={onChange} testId={testId} />
      <Input className={inputCls} placeholder="Full address" value={value.address || ""} onChange={(e) => onChange({ ...value, address: e.target.value })} data-testid={`${testId}-address`} />
    </div>
  );
}

export function LegDetailsDialog({ i, t, onClose, onSaved }) {
  const api = useApi();
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (i === null) return;
    const lg = t.legs[i];
    setF({ dep_fbo: lg.dep_fbo, arr_fbo: lg.arr_fbo, catering: lg.catering, ground: lg.ground, track_url: lg.track_url || "" });
  }, [i, t]);
  if (i === null || !f) return null;
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const tail = t.aircraft.tail_number;
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.patch(`/tracking/${t.id}/legs/${i}`, f);
      toast.success("Trip details saved");
      onSaved(data);
      onClose();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[#111319] border-white/10 max-w-xl max-h-[92vh] overflow-y-auto" data-testid="leg-details-dialog">
        <DialogHeader>
          <p className="eyebrow">Leg {i + 1} · {code(t.legs[i].from_airport)} → {code(t.legs[i].to_airport)}</p>
          <DialogTitle className="font-serif-display text-3xl font-normal text-white">Trip details</DialogTitle>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4 mt-2">
          <Field label="Live tracking link">
            <Input className={inputCls} placeholder="https://…" value={f.track_url} onChange={(e) => set("track_url", e.target.value)} data-testid="track-url-input" />
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2">
              {tail && <button type="button" className="text-[11px] text-[#5B7FD1]" onClick={() => set("track_url", `https://www.flightaware.com/live/flight/${encodeURIComponent(tail)}`)} data-testid="track-url-flightaware-btn">Use FlightAware link for {tail}</button>}
              <span className="text-[11px] text-zinc-500">The broker gets an email when a new link is added.</span>
            </div>
          </Field>
          <Field label="Departure FBO"><FboRow value={f.dep_fbo} onChange={(v) => set("dep_fbo", v)} testId="dep-fbo" /></Field>
          <Field label="Arrival FBO"><FboRow value={f.arr_fbo} onChange={(v) => set("arr_fbo", v)} testId="arr-fbo" /></Field>
          <Field label="Catering"><Textarea className="bg-[#0d0f14] border-white/10" value={f.catering} onChange={(e) => set("catering", e.target.value)} data-testid="catering-input" /></Field>
          <Field label="Ground transport"><Textarea className="bg-[#0d0f14] border-white/10" value={f.ground} onChange={(e) => set("ground", e.target.value)} data-testid="ground-input" /></Field>
          <div className="flex justify-end gap-3">
            <button type="button" className="frame-btn" onClick={onClose} data-testid="details-cancel-btn">Cancel</button>
            <button type="submit" className="frame-btn gold" disabled={busy} data-testid="details-save-btn">{busy ? "Saving…" : "Save details"}</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
